import { useState, useEffect, useMemo, useCallback } from 'react';
import { FileText, Plus, Trash2, Send, CheckCircle, Search, ChevronDown, ChevronUp } from 'lucide-react';import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../../components/ui/Pagination';

const ITEMS_PER_PAGE = 10;

export default function AdminQuotes() {
  const { token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [paymentStep, setPaymentStep] = useState('full'); // 'full', 'deposit', 'midway', 'final'
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [formData, setFormData] = useState({ user_id: '', title: '', description: '', items: [], amount: '', payment_type: 'deposit' });
  const [useItems, setUseItems] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [paymentData, setPaymentData] = useState({ payment_method: 'virement', notes: '' });
  const [creating, setCreating] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const paginatedQuotes = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return quotes.slice(start, start + ITEMS_PER_PAGE);
  }, [quotes, currentPage]);

  const totalPages = Math.ceil(quotes.length / ITEMS_PER_PAGE);

  const fetchData = useCallback(async () => {
    try {
      const [quotesRes, clientsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/quotes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/clients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const quotesData = await quotesRes.json();
      const clientsData = await clientsRes.json();
      setQuotes(quotesData);
      setClients(clientsData);
      setFilteredClients(clientsData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClientSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client => {
        const name = client.billing_info 
          ? `${client.billing_info.first_name} ${client.billing_info.last_name}`.toLowerCase()
          : '';
        const email = client.email.toLowerCase();
        const company = client.billing_info?.company_name?.toLowerCase() || '';
        return name.includes(query.toLowerCase()) || 
               email.includes(query.toLowerCase()) ||
               company.includes(query.toLowerCase());
      });
      setFilteredClients(filtered);
    }
    setShowClientDropdown(true);
  };

  const selectClient = (client) => {
    setFormData({ ...formData, user_id: client.id });
    const displayName = client.billing_info 
      ? `${client.billing_info.first_name} ${client.billing_info.last_name}`.trim() + ` (${client.email})`
      : client.email;
    setSearchQuery(displayName);
    setShowClientDropdown(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    
    try {
      const computedAmount = useItems
        ? formData.items.reduce((acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0)
        : parseFloat(formData.amount);
      const cleanedItems = useItems
        ? formData.items
            .filter((it) => (it.name || '').trim())
            .map((it) => ({
              name: it.name,
              description: it.description || '',
              quantity: Number(it.quantity) || 1,
              unit: it.unit || 'forfait',
              unit_price: Number(it.unit_price) || 0,
            }))
        : [];
      const res = await fetch(`${API_URL}/api/admin/quotes`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          items: cleanedItems,
          amount: computedAmount,
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Erreur');
      }
      
      setShowCreateForm(false);
      setFormData({ user_id: '', title: '', description: '', items: [], amount: '', payment_type: 'deposit' });
      setUseItems(false);
      setSearchQuery('');
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmPayment = async (e) => {
    e.preventDefault();
    if (!selectedQuote) return;
    setConfirmingPayment(true);
    setError('');
    
    try {
      // Determine endpoint based on payment step
      let endpoint = `${API_URL}/api/admin/quotes/${selectedQuote.id}/confirm-payment`;
      if (paymentStep === 'deposit') {
        endpoint = `${API_URL}/api/admin/quotes/${selectedQuote.id}/confirm-deposit`;
      } else if (paymentStep === 'final') {
        endpoint = `${API_URL}/api/admin/quotes/${selectedQuote.id}/confirm-final`;
      } else if (paymentStep === 'full') {
        endpoint = `${API_URL}/api/admin/quotes/${selectedQuote.id}/confirm-full`;
      }
      
      const res = await fetch(endpoint, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Erreur');
      }
      
      setShowPaymentModal(false);
      setSelectedQuote(null);
      setPaymentData({ payment_method: 'virement', notes: '' });
      setPaymentStep('full');
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirmingPayment(false);
    }
  };

  const openPaymentModal = (quote, step = 'full') => {
    setSelectedQuote(quote);
    setPaymentStep(step);
    setPaymentData({ payment_method: 'virement', notes: '' });
    setShowPaymentModal(true);
  };

  const getNextPaymentStep = (quote) => {
    if (quote.status === 'paid') return null;
    if (quote.payment_type === 'full') return 'full';
    if (!quote.deposit_paid) return 'deposit';
    if (!quote.final_paid) return 'final';
    return null;
  };

  const getPaymentStepLabel = (step, quote) => {
    const total = quote?.amount || 0;
    switch(step) {
      case 'deposit': return `Acompte (30%) - ${Math.round(total * 0.30 * 100) / 100}€`;
      case 'final': return `Solde (70%) - ${Math.round(total * 0.70 * 100) / 100}€`;
      case 'full': return `Paiement intégral - ${total}€`;
      default: return '';
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Supprimér ce devis ?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/quotes/${quoteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      deposit_paid: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      midway_paid: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    const labels = { 
      paid: 'Payé', 
      pending: 'En attente', 
      deposit_paid: 'Acompte versé',
      midway_paid: 'Acompte versé',
      cancelled: 'Annulé' 
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const PaymentProgress = ({ quote }) => {
    const total = quote.amount;
    
    if (quote.payment_type === 'full') {
      return (
        <div className="text-xs space-y-1 mt-2">
          <div className="flex items-center gap-2">
            <span className={quote.status === 'paid' ? 'text-green-600' : 'text-muted-foreground'}>
              {quote.status === 'paid' ? '✓' : '○'}
            </span>
            <span>Paiement intégral : {total}€</span>
          </div>
        </div>
      );
    }
    
    const depositAmount = quote.deposit_amount || Math.round(total * 0.30 * 100) / 100;
    const finalAmount = quote.final_amount || Math.round(total * 0.70 * 100) / 100;
    
    return (
      <div className="text-xs space-y-1 mt-2">
        <div className="flex items-center gap-2">
          <span className={quote.deposit_paid ? 'text-green-600' : 'text-muted-foreground'}>
            {quote.deposit_paid ? '✓' : '○'}
          </span>
          <span>Acompte 30% : {depositAmount}€</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={quote.final_paid ? 'text-green-600' : 'text-muted-foreground'}>
            {quote.final_paid ? '✓' : '○'}
          </span>
          <span>Solde 70% : {finalAmount}€</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="animate-pulse h-64 bg-muted rounded-xl" />;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Devis</h1>
          <p className="text-muted-foreground">{quotes.length} devis</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="btn-primary">
          <Plus className="mr-2 h-4 w-4" /> Nouveau devis
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Créer un devis</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              {error && (
                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">{error}</div>
              )}
              
              <div className="space-y-2 relative">
                <label className="text-sm font-medium">Client *</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-10"
                    placeholder="Rechercher un client par nom, email ou entreprise..."
                    value={searchQuery}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    onFocus={() => setShowClientDropdown(true)}
                  />
                </div>
                {showClientDropdown && filteredClients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        className="px-4 py-3 hover:bg-muted cursor-pointer border-b border-border last:border-0"
                        onClick={() => selectClient(client)}
                      >
                        <p className="font-medium">
                          {client.billing_info 
                            ? `${client.billing_info.first_name} ${client.billing_info.last_name}`.trim()
                            : client.email
                          }
                        </p>
                        <p className="text-sm text-muted-foreground">{client.email}</p>
                        {client.billing_info?.company_name && (
                          <p className="text-xs text-muted-foreground">{client.billing_info.company_name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {showClientDropdown && searchQuery && filteredClients.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                    Aucun client trouve
                  </div>
                )}
              </div>
              
              <Input
                placeholder="Titre du devis *"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
              
              <textarea
                className="w-full min-h-24 px-3 py-2 rounded-md border border-input bg-background resize-none font-mono leading-relaxed"
                placeholder="Description du devis (optionnel)&#10;&#10;Astuce : commencez une ligne par « - » ou « • » pour créer une puce.&#10;&#10;Exemple :&#10;Prestation complète comprenant :&#10;- Maquette UI/UX&#10;- Intégration responsive&#10;- Mise en ligne et formation&#10;- 1 an d'hébergement offert"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={6}
              />
              <p className="text-xs text-muted-foreground -mt-2">
                Cette description sera visible par le client et reprise dans le PDF de facture.
              </p>

              {/* ========== ITEMS / LIGNES DÉTAILLÉES ========== */}
              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={useItems}
                      onChange={(e) => {
                        setUseItems(e.target.checked);
                        if (!e.target.checked) {
                          setFormData((f) => ({ ...f, items: [] }));
                        } else if (formData.items.length === 0) {
                          setFormData((f) => ({ ...f, items: [{ name: '', description: '', quantity: 1, unit: 'forfait', unit_price: 0 }] }));
                        }
                      }}
                      className="h-4 w-4"
                      data-testid="use-items-toggle"
                    />
                    Détailler les lignes du devis (auto-calcul du montant)
                  </label>
                  {useItems && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData((f) => ({ ...f, items: [...f.items, { name: '', description: '', quantity: 1, unit: 'forfait', unit_price: 0 }] }))}
                      data-testid="add-quote-item"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Ligne
                    </Button>
                  )}
                </div>

                {useItems && formData.items.map((item, idx) => (
                  <div key={idx} className="border border-border rounded-lg p-3 bg-muted/30 space-y-2" data-testid={`quote-item-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase text-muted-foreground">Ligne {idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => setFormData((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                        className="text-destructive hover:bg-destructive/10 p-1 rounded"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <Input
                      placeholder="Désignation (ex. Création du site web)"
                      value={item.name}
                      onChange={(e) => {
                        const items = [...formData.items];
                        items[idx] = { ...items[idx], name: e.target.value };
                        setFormData({ ...formData, items });
                      }}
                    />
                    <textarea
                      className="w-full min-h-16 px-3 py-2 rounded-md border border-input bg-background resize-none text-sm font-mono leading-relaxed"
                      placeholder="Description détaillée (optionnel, utilisez - ou • pour des puces)"
                      value={item.description || ''}
                      onChange={(e) => {
                        const items = [...formData.items];
                        items[idx] = { ...items[idx], description: e.target.value };
                        setFormData({ ...formData, items });
                      }}
                      rows={3}
                    />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground">Quantité</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => {
                            const items = [...formData.items];
                            items[idx] = { ...items[idx], quantity: parseFloat(e.target.value) || 0 };
                            setFormData({ ...formData, items });
                          }}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Unité</label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={item.unit || 'forfait'}
                          onChange={(e) => {
                            const items = [...formData.items];
                            items[idx] = { ...items[idx], unit: e.target.value };
                            setFormData({ ...formData, items });
                          }}
                        >
                          <option value="forfait">forfait</option>
                          <option value="h">heure</option>
                          <option value="j">jour</option>
                          <option value="u">unité</option>
                          <option value="km">km</option>
                          <option value="lot">lot</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">PU HT (€)</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => {
                            const items = [...formData.items];
                            items[idx] = { ...items[idx], unit_price: parseFloat(e.target.value) || 0 };
                            setFormData({ ...formData, items });
                          }}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Total</label>
                        <div className="h-10 flex items-center px-3 rounded-md bg-background border border-input text-sm font-semibold">
                          {((Number(item.quantity) || 0) * (Number(item.unit_price) || 0)).toFixed(2)} €
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {useItems && formData.items.length > 0 && (
                  <div className="text-right text-sm font-bold pt-2 border-t border-border/50">
                    Total auto-calculé : {formData.items.reduce((acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0).toFixed(2)} €
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Montant total (€) *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="1500.00"
                  value={useItems ? formData.items.reduce((acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0).toFixed(2) : formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  required
                  disabled={useItems}
                />
                {useItems && (
                  <p className="text-xs text-muted-foreground">Le montant est calculé automatiquement à partir des lignes.</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Type de paiement</label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.payment_type}
                  onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                >
                  <option value="deposit">Avec acompte (30% + 70%)</option>
                  <option value="full">Paiement intégral (100%)</option>
                </select>
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" disabled={creating || !formData.user_id} className="btn-primary">
                  <Send className="mr-2 h-4 w-4" />
                  {creating ? 'Création...' : 'Créer le devis'}
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  setShowCreateForm(false);
                  setSearchQuery('');
                  setUseItems(false);
                  setFormData({ user_id: '', title: '', description: '', items: [], amount: '', payment_type: 'deposit' });
                }}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Quotes List */}
      <Card>
        <CardContent className="p-0">
          {quotes.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun devis</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-4 font-medium text-muted-foreground">Client</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Titre</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Montant</th>
                    <th className="text-center p-4 font-medium text-muted-foreground">Statut</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedQuotes.map((quote) => {
                    const nextStep = getNextPaymentStep(quote);
                    const isExpanded = expandedQuote === quote.id;
                    return (
                    <tr key={quote.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{quote.client_name || '-'}</p>
                          <p className="text-sm text-muted-foreground">{quote.client_email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="font-medium">{quote.title}</p>
                        {quote.description && (
                          <p className="text-sm text-muted-foreground truncate max-w-xs">{quote.description}</p>
                        )}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        <div>
                          {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                          {quote.valid_until && quote.status === 'pending' && (
                            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                              Expire le {new Date(quote.valid_until).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-right">
                          <p className="font-semibold">{quote.amount}€</p>
                          {quote.status !== 'paid' && quote.status !== 'pending' && (
                            <button 
                              onClick={() => setExpandedQuote(isExpanded ? null : quote.id)}
                              className="text-xs text-primary hover:underline flex items-center gap-1 ml-auto"
                            >
                              Détails {isExpanded ? <ChevronUp className="h-3 w-3"/> : <ChevronDown className="h-3 w-3"/>}
                            </button>
                          )}
                          {isExpanded && <PaymentProgress quote={quote} />}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <StatusBadge status={quote.status} />
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {quote.status !== 'paid' && nextStep && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openPaymentModal(quote, nextStep)}
                              className="text-green-600 border-green-600 hover:bg-green-50"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              {nextStep === 'deposit' && 'Acompte reçu'}
                              {nextStep === 'final' && 'Solde reçu'}
                              {nextStep === 'full' && 'Paiement reçu'}
                            </Button>
                          )}
                          {quote.status === 'pending' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDelete(quote.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={quotes.length}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </CardContent>
      </Card>

      {/* Manual Payment Confirmation Modal */}
      {showPaymentModal && selectedQuote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Confirmer le paiement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConfirmPayment} className="space-y-4">
                {error && (
                  <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">{error}</div>
                )}
                
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-medium">{selectedQuote.title}</p>
                  <p className="text-sm text-muted-foreground">{selectedQuote.client_name || selectedQuote.client_email}</p>
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-sm text-muted-foreground">Étape de paiement</p>
                    <p className="text-xl font-bold text-green-600">
                      {getPaymentStepLabel(paymentStep, selectedQuote)}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Méthode de paiement *</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={paymentData.payment_method}
                    onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
                    required
                  >
                    <option value="virement">Virement bancaire</option>
                    <option value="especes">Espèces</option>
                    <option value="cheque">Chèque</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Notes (optionnel)</label>
                  <textarea
                    className="w-full min-h-20 px-3 py-2 rounded-md border border-input bg-background resize-none"
                    placeholder="Référence de virement, numéro de chèque..."
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                  />
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={() => {
                      setShowPaymentModal(false);
                      setSelectedQuote(null);
                      setPaymentStep('full');
                      setError('');
                    }}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit"
                    className="btn-primary"
                    disabled={confirmingPayment}
                  >
                    {confirmingPayment ? 'Confirmation...' : 'Confirmer'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
